import { useState, useEffect, useLayoutEffect, useCallback, useRef} from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import { CATEGORIES, guessCategory } from '../categories';
import PiPrice from './PiPrice';
import { toPiFrom, CURRENCY_LIST, getPiConversion } from './piCurrency';
import { t } from '../i18n';

const API_BASE = '/api/merchants';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

const colors = {
  bg: '#F7F5F0',
  surface: '#fff',
  border: '#E3E1D9',
  text: '#1F1E1B',
  textMuted: '#5F5E5A',
  primary: '#0F6E56',
  primaryBg: '#E1F5EE',
  danger: '#A32D2D',
};

function ProductCard({ product, onAddToCart }) {
  const [showReviews, setShowReviews] = useState(false);
  const [reviews, setReviews] = useState(null);

  const toggleReviews = async () => {
    if (!showReviews && reviews === null) {
      try {
        const res = await fetch(`${API_BASE}/products/${product.id}/reviews/`);
        if (res.ok) setReviews(await safeJson(res));
      } catch (e) {}
    }
    setShowReviews((s) => !s);
  };

  return (
    <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ width: '100%', aspectRatio: '1', background: '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {product.image ? (
          <img src={product.image} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 32, opacity: 0.3 }}>📦</span>
        )}
      </div>
      <div style={{ padding: '0.75rem' }}>
        {product.matched_interest && (
          <span style={{ fontSize: 10, background: colors.primaryBg, color: colors.primary, padding: '2px 6px', borderRadius: 8, marginBottom: 4, display: 'inline-block' }}>
            ✨ For you
          </span>
        )}
        {product.is_boosted && (
          <span style={{ fontSize: 10, background: '#FFF3C4', color: '#8A6D00', padding: '2px 6px', borderRadius: 8, marginBottom: 4, display: 'inline-block' }}>
            📢 Sponsored
          </span>
        )}
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {product.product_type === 'virtual' ? '💾 ' : ''}{product.name}
        </p>
        <a href={`/merchants/${product.merchant_slug}`} style={{ fontSize: 12, color: colors.textMuted, textDecoration: 'none' }}>
          {product.merchant_name}
        </a>
        {product.merchant_discount_percent > 0 ? (
          <p style={{ margin: '6px 0' }}>
            <span style={{ fontSize: 12, color: colors.textMuted, textDecoration: 'line-through', marginRight: 6 }}><PiPrice pi={product.price_pi} suffix="" /></span>
            <span style={{ fontSize: 16, fontWeight: 600, color: colors.primary }}>
              {(product.price_pi * (1 - product.merchant_discount_percent / 100)).toFixed(2)} Pi
            </span>
            <span style={{ fontSize: 10, background: '#FFE4E1', color: colors.danger, padding: '2px 6px', borderRadius: 8, marginLeft: 6 }}>
              -{product.merchant_discount_percent}%
            </span>
          </p>
        ) : (
          <p style={{ fontSize: 16, fontWeight: 600, color: colors.primary, margin: '6px 0' }}><PiPrice pi={product.price_pi} /></p>
        )}
        <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 8px' }}>
          {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
        </p>
        <button
          onClick={() => onAddToCart(product)}
          disabled={product.stock <= 0}
          style={{
            width: '100%', padding: '8px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 6,
            background: product.stock > 0 ? colors.primary : '#ccc', color: '#fff',
            cursor: product.stock > 0 ? 'pointer' : 'default',
          }}
        >
          {product.stock > 0 ? 'Add to cart' : 'Out of stock'}
        </button>
        <button
          onClick={toggleReviews}
          style={{ width: '100%', marginTop: 6, padding: '4px', fontSize: 11, border: 'none', background: 'transparent', color: colors.textMuted, cursor: 'pointer' }}
        >
          {reviews ? `⭐ ${reviews.avg_rating} (${reviews.total_reviews})` : '⭐ Reviews'}
        </button>
        {product.merchant_username && (
          <button
            onClick={(e) => { e.stopPropagation(); window.location.href = `/chat/${product.merchant_username}?about=${encodeURIComponent(product.name)}`; }}
            style={{ fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer', marginLeft: 6 }}
          >
            💬 {t('chat_with_seller')}
          </button>
        )}
        {showReviews && reviews && (
          <div style={{ marginTop: 4, maxHeight: 100, overflowY: 'auto' }}>
            {reviews.reviews.length === 0 ? (
              <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{t('no_reviews_yet')}</p>
            ) : (
              reviews.reviews.map((r, i) => (
                <p key={i} style={{ fontSize: 11, color: colors.textMuted, margin: '2px 0' }}>
                  @{r.reviewer} {'⭐'.repeat(r.rating)} {r.comment}
                </p>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CartPanel({ cart, onUpdateQty, onRemove, onCheckout, checkingOut, voucherCode, setVoucherCode, shippingPhone, setShippingPhone, shippingAddress, setShippingAddress, buyerEmail, setBuyerEmail }) {
  const hasPhysical = cart.items.some((it) => it.product_type !== 'virtual');

  if (cart.items.length === 0) {
    return <p style={{ color: colors.textMuted, fontSize: 14, textAlign: 'center', padding: '2rem 0' }}>{t('cart_empty')}</p>;
  }

  return (
    <div>
      {cart.items.map((item) => (
        <div key={item.product_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `0.5px solid ${colors.border}` }}>
          <div style={{ width: 48, height: 48, background: '#F1EFE8', borderRadius: 8, flexShrink: 0, overflow: 'hidden' }}>
            {item.image && <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 500, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</p>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}>{item.merchant_name} · <PiPrice pi={item.price_pi} suffix=" each" /></p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => onUpdateQty(item.product_id, item.quantity - 1)} style={{ width: 24, height: 24, border: `0.5px solid ${colors.border}`, borderRadius: 4, background: '#fff', cursor: 'pointer' }}>-</button>
            <span style={{ fontSize: 13, minWidth: 16, textAlign: 'center' }}>{item.quantity}</span>
            <button onClick={() => onUpdateQty(item.product_id, item.quantity + 1)} style={{ width: 24, height: 24, border: `0.5px solid ${colors.border}`, borderRadius: 4, background: '#fff', cursor: 'pointer' }}>+</button>
          </div>
          <button onClick={() => onRemove(item.product_id)} style={{ fontSize: 12, color: colors.danger, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      ))}
      {hasPhysical && (
        <div style={{ marginTop: 12, background: '#F1EFE8', borderRadius: 8, padding: '0.75rem' }}>
          <p style={{ fontSize: 12, fontWeight: 500, margin: '0 0 8px' }}>📦 Shipping details (required for physical items)</p>
          <input
            placeholder={t('phone_number')}
            value={shippingPhone}
            onChange={(e) => setShippingPhone(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `0.5px solid #B4B2A9`, borderRadius: 6, boxSizing: 'border-box', marginBottom: 8 }}
          />
          <textarea
            placeholder={t('delivery_address')}
            value={shippingAddress}
            onChange={(e) => setShippingAddress(e.target.value)}
            rows={2}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `0.5px solid #B4B2A9`, borderRadius: 6, boxSizing: 'border-box', resize: 'vertical' }}
          />
          <input
            type="email"
            placeholder={t('email_for_receipt')}
            value={buyerEmail}
            onChange={(e) => setBuyerEmail(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `0.5px solid #B4B2A9`, borderRadius: 6, boxSizing: 'border-box', marginTop: 8 }}
          />
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <input
          placeholder={t('voucher_code')}
          value={voucherCode}
          onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
          style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `0.5px solid #B4B2A9`, borderRadius: 6, boxSizing: 'border-box', marginBottom: 8 }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: colors.textMuted, padding: '4px 0' }}>
        <span>{t('subtotal')}</span>
        {/* CART_TOTAL_CURRENCY */}
        <span style={{ textAlign: 'right' }}><PiPrice pi={cart.total_pi} /></span>
      </div>
      {hasPhysical && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: colors.textMuted, padding: '4px 0' }}>
          <span>Shipping (estimate)</span>
          <span style={{ textAlign: 'right' }}><PiPrice pi={cart.items.filter((it) => it.product_type !== 'virtual').reduce((s, it) => s + (it.shipping_fee_pi || 0), 0)} /></span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', fontWeight: 600, fontSize: 15, borderTop: `0.5px solid ${colors.border}` }}>
        <span>Total (approx.)</span>
        <span style={{ textAlign: 'right' }}><PiPrice pi={cart.total_pi + cart.items.filter((it) => it.product_type !== 'virtual').reduce((s, it) => s + (it.shipping_fee_pi || 0), 0)} style={{ fontWeight: 600 }} /></span>
      </div>
      <button
        onClick={onCheckout}
        disabled={checkingOut}
        style={{
          width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8,
          background: colors.primary, color: '#fff', cursor: checkingOut ? 'default' : 'pointer', opacity: checkingOut ? 0.7 : 1,
        }}
      >
        {checkingOut ? 'Processing...' : 'Checkout with Pi'}
      </button>
    </div>
  );
}


// ==================== Scratch card ====================
function ScratchCard({ code, revealed, onReveal }) {
  const ref = useRef(null);
  const [done, setDone] = useState(!!revealed);
  const [covered, setCovered] = useState(false);
  const drawing = useRef(false);
  const ticks = useRef(0);

  useLayoutEffect(() => {
    if (done) return;
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, cv.width, cv.height);
    g.addColorStop(0, '#9aa5a1');
    g.addColorStop(0.5, '#c2ccc8');
    g.addColorStop(1, '#9aa5a1');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = 'rgba(255,255,255,.6)';
    ctx.font = '600 13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Scratch here', cv.width / 2, cv.height / 2 + 4);
    ctx.globalCompositeOperation = 'destination-out';
    setCovered(true);
  }, [done]);

  const scratch = (e) => {
    if (!drawing.current || done) return;
    if (e.cancelable) e.preventDefault();
    const cv = ref.current;
    const ctx = cv.getContext('2d');
    const r = cv.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    const x = (p.clientX - r.left) * (cv.width / r.width);
    const y = (p.clientY - r.top) * (cv.height / r.height);
    ctx.beginPath();
    ctx.arc(x, y, 16, 0, Math.PI * 2);
    ctx.fill();
    ticks.current += 1;
    if (ticks.current % 12 !== 0) return;
    const img = ctx.getImageData(0, 0, cv.width, cv.height).data;
    let clear = 0, total = 0;
    for (let i = 3; i < img.length; i += 40) { total += 1; if (img[i] < 40) clear += 1; }
    if (total && clear / total > 0.55) { setDone(true); if (onReveal) onReveal(); }
  };

  const codeStyle = { fontFamily: 'monospace', fontSize: 16, fontWeight: 700, letterSpacing: 1, color: '#fff', margin: 0, wordBreak: 'break-all' };

  if (done) {
    return (
      <div style={{ background: '#0F6E56', borderRadius: 10, padding: '14px 12px', textAlign: 'center' }}>
        <p style={codeStyle}>{code}</p>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#0F6E56' }}>
      {covered && (
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: '0 10px' }}>
        <p style={{ ...codeStyle, textAlign: 'center' }}>{code}</p>
      </div>
      )}
      <canvas ref={ref} width={320} height={64}
        style={{ position: 'relative', width: '100%', height: 64, display: 'block', cursor: 'grab', touchAction: 'none' }}
        onMouseDown={(e) => { drawing.current = true; scratch(e); }}
        onMouseMove={scratch}
        onMouseUp={() => { drawing.current = false; }}
        onMouseLeave={() => { drawing.current = false; }}
        onTouchStart={(e) => { drawing.current = true; scratch(e); }}
        onTouchMove={scratch}
        onTouchEnd={() => { drawing.current = false; }} />
    </div>
  );
}

function DigitalItemsModal({ orderId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/orders/${orderId}/digital-items/`, { headers: authHeaders() })
      .then(async (r) => {
        const d = await safeJson(r);
        if (!r.ok) throw new Error(d.error || 'Failed');
        setData(d);
      })
      .catch((e) => setErr(e.message));
  }, [orderId]);

  const copy = (txt, id) => {
    navigator.clipboard.writeText(txt).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1600);
    }).catch(() => {});
  };
  const reveal = (id) => {
    fetch(`${API_BASE}/codes/${id}/reveal/`, { method: 'POST', headers: authHeaders() }).catch(() => {});
  };
  const copyBtn = (txt, id) => (
    <button onClick={() => copy(txt, id)}
      style={{ fontSize: 11, padding: '4px 10px', border: '0.5px solid #B4B2A9', borderRadius: 7, background: '#fff', cursor: 'pointer', marginTop: 5 }}>
      {copied === id ? `✅ ${t('dig_copied')}` : `📋 ${t('dig_copy')}`}
    </button>
  );
  const codeBox = { background: '#0F6E56', borderRadius: 10, padding: '14px 12px', textAlign: 'center' };
  const codeTxt = { fontFamily: 'monospace', fontSize: 16, fontWeight: 700, letterSpacing: 1, color: '#fff', margin: 0, wordBreak: 'break-all' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', maxWidth: 420, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, margin: 0 }}>💾 {t('dig_your_items')}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#5F5E5A' }}>×</button>
        </div>
        {err && <p style={{ fontSize: 13, color: '#C0392B' }}>{err}</p>}
        {!data && !err && <p style={{ fontSize: 13, color: '#5F5E5A' }}>…</p>}
        {data && data.items.map((it) => (
          <div key={it.product_id} style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 14, fontWeight: 600, margin: '0 0 6px' }}>{it.name}</p>
            {it.digital_kind === 'shared' ? (
              <div style={{ background: '#F4F3EE', borderRadius: 10, padding: '10px 12px' }}>
                <p style={{ fontFamily: 'monospace', fontSize: 13, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{it.content}</p>
                {copyBtn(it.content, 'sh' + it.product_id)}
              </div>
            ) : (
              <>
                {it.content && <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 6px', whiteSpace: 'pre-wrap' }}>{it.content}</p>}
                {it.codes.length === 0 && <p style={{ fontSize: 12, color: '#C0392B' }}>{t('dig_no_code_yet')}</p>}
                {it.codes.map((cd) => (
                  <div key={cd.id} style={{ marginBottom: 8 }}>
                    {it.digital_kind === 'scratch'
                      ? <ScratchCard code={cd.code} revealed={cd.revealed} onReveal={() => reveal(cd.id)} />
                      : <div style={codeBox}><p style={codeTxt}>{cd.code}</p></div>}
                    {copyBtn(cd.code, cd.id)}
                  </div>
                ))}
              </>
            )}
          </div>
        ))}
        <p style={{ fontSize: 11, color: '#5F5E5A', margin: '10px 0 0', borderTop: '0.5px solid #E3E1D9', paddingTop: 10 }}>{t('dig_saved_note')}</p>
      </div>
    </div>
  );
}

function CodesPanel({ product, onClose }) {
  const [info, setInfo] = useState(null);
  const [txt, setTxt] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API_BASE}/products/${product.id}/codes/`, { headers: authHeaders() })
      .then((r) => r.json()).then(setInfo).catch(() => {});
  }, [product.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!txt.trim()) { setMsg(t('dig_paste_first')); return; }
    setBusy(true); setMsg('');
    try {
      const r = await fetch(`${API_BASE}/products/${product.id}/codes/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ codes: txt }),
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d.error || 'Failed');
      setMsg(`✅ ${d.added} ${t('dig_added')}${d.skipped_duplicates ? ` · ${d.skipped_duplicates} ${t('dig_dupes')}` : ''}`);
      setTxt('');
      load();
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };

  const clearUnsold = async () => {
    if (!window.confirm(t('dig_clear_confirm'))) return;
    await fetch(`${API_BASE}/products/${product.id}/codes/`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  const lineCount = txt.split('\n').filter((x) => x.trim()).length;
  const box = { flex: 1, borderRadius: 8, padding: '8px 10px' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: '1.25rem', maxWidth: 440, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ fontSize: 16, margin: 0 }}>🎟️ {t('dig_codes')}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#5F5E5A' }}>×</button>
        </div>
        <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 12px' }}>{product.name}</p>
        {info && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ ...box, background: '#EAF6F1' }}>
              <p style={{ fontSize: 10, color: '#5F5E5A', margin: 0 }}>{t('dig_available')}</p>
              <p style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#0F6E56' }}>{info.available}</p>
            </div>
            <div style={{ ...box, background: '#F4F3EE' }}>
              <p style={{ fontSize: 10, color: '#5F5E5A', margin: 0 }}>{t('dig_sold')}</p>
              <p style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{info.sold}</p>
            </div>
          </div>
        )}
        {info && info.available === 0 && <p style={{ fontSize: 12, color: '#C0392B', margin: '0 0 10px' }}>⚠️ {t('dig_out_of_stock')}</p>}
        <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>{t('dig_paste_label')}</label>
        <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={6}
          style={{ width: '100%', padding: '9px 10px', border: '0.5px solid #B4B2A9', borderRadius: 8, fontSize: 13, fontFamily: 'monospace', resize: 'vertical' }} />
        <p style={{ fontSize: 10, color: '#5F5E5A', margin: '4px 0 10px' }}>{lineCount} {t('dig_lines')} · {t('dig_one_per_buyer')}</p>
        {msg && <p style={{ fontSize: 12, color: msg.startsWith('✅') ? '#0F6E56' : '#C0392B', margin: '0 0 8px' }}>{msg}</p>}
        <button onClick={add} disabled={busy}
          style={{ fontSize: 13, padding: '8px 16px', border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
          {busy ? '…' : t('dig_add_codes')}
        </button>
        {info && info.available > 0 && (
          <button onClick={clearUnsold}
            style={{ fontSize: 12, padding: '8px 12px', border: '0.5px solid #C0392B', borderRadius: 8, background: '#fff', color: '#C0392B', cursor: 'pointer', marginLeft: 8 }}>
            {t('dig_clear_unsold')}
          </button>
        )}
        {info && info.recent && info.recent.length > 0 && (
          <div style={{ marginTop: 16, borderTop: '0.5px solid #E3E1D9', paddingTop: 10 }}>
            <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 6px' }}>{t('dig_recent')}</p>
            {info.recent.slice(0, 12).map((cd) => (
              <div key={cd.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0', borderBottom: '0.5px solid #F4F3EE' }}>
                <span style={{ fontFamily: 'monospace' }}>{cd.code}</span>
                <span style={{ color: '#5F5E5A' }}>{cd.buyer ? `@${cd.buyer} · ${cd.assigned_at}` : t('dig_unsold')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MyProductsPanel({ myProducts, onProductAdded }) {
  const [codesFor, setCodesFor] = useState(null);
  const [quickEditFor, setQuickEditFor] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price_pi: '', category: '', stock: 1, product_type: 'physical', digital_content: '', digital_kind: 'shared', shipping_fee_pi: '' });
  const [catTouched, setCatTouched] = useState(false);
  const [priceCurrency, setPriceCurrency] = useState('PI');
  const [priceInput, setPriceInput] = useState('');
  const [pricePreview, setPricePreview] = useState(null);
  const [catSuggest, setCatSuggest] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [boosting, setBoosting] = useState({});

  const boostProduct = async (productId, productName) => {
    if (!window.Pi) {
      alert('Open this page inside Pi Browser to pay with Pi.');
      return;
    }
    const boostAmount = 1; // flat 1 Pi for a 7-day boost
    setBoosting((b) => ({ ...b, [productId]: true }));

    try {
      await window.Pi.authenticate(['username', 'payments'], (payment) => {
        console.log('Incomplete payment found during re-auth:', payment);
      });

      window.Pi.createPayment(
        {
          amount: boostAmount,
          memo: `Boost product: ${productName}`,
          metadata: { product_id: productId },
        },
        {
          onReadyForServerApproval: (paymentId) => {
            fetch(`${API_BASE}/products/${productId}/boost/approve/`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ payment_id: paymentId }),
            });
          },
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/products/${productId}/boost/complete/`, {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({ payment_id: paymentId, txid, amount: boostAmount, days: 7 }),
            })
              .then(() => {
                alert('Product boosted for 7 days!');
                setBoosting((b) => ({ ...b, [productId]: false }));
                onProductAdded();
              })
              .catch(() => {
                alert('Payment reached Pi but boost activation failed. Contact support.');
                setBoosting((b) => ({ ...b, [productId]: false }));
              });
          },
          onCancel: () => setBoosting((b) => ({ ...b, [productId]: false })),
          onError: () => setBoosting((b) => ({ ...b, [productId]: false })),
        }
      );
    } catch (e) {
      alert('Could not get payment permission from Pi.');
      setBoosting((b) => ({ ...b, [productId]: false }));
    }
  };

    // Ye categories digital hoti hain — inhe chuno to product_type khud 'virtual'.
  // (Scratch card, gift card, software, course, digital file — koi courier nahi.)
  const DIGITAL_CATS = ['giftcards', 'software', 'digital_goods', 'courses'];

const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async () => {
    // Saaf jaanch — button disabled ke bawajood, taake wajah maloom ho
    if (!form.name || !form.name.trim()) { setMsg(t('mm_need_name')); return; }
    if (!form.price_pi || Number(form.price_pi) <= 0) { setMsg(t('mm_need_price')); return; }
    if (form.product_type === 'virtual' && form.digital_kind === 'shared'
        && (!form.digital_content || !form.digital_content.trim())) {
      setMsg(t('mm_need_content')); return;
    }
    setSubmitting(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/products/create/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        alert('Error: ' + (data.error || 'Could not create product.'));
        throw new Error(data.error || 'Could not create product.');
      }

      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        const token = getAuthToken();
        await fetch(`${API_BASE}/products/${data.id}/upload-image/`, {
          method: 'POST',
          headers: token ? { Authorization: `Token ${token}` } : {},
          body: formData,
        });
      }

      alert('Product added successfully!');
      setMsg('Product added!');
      setForm({ name: '', description: '', price_pi: '', category: '', stock: 1, product_type: 'physical', digital_content: '', digital_kind: 'shared', shipping_fee_pi: '' });
      setImageFile(null);
      onProductAdded();
    } catch (e) {
      if (!e.message.includes('Could not create')) {
        setMsg('⚠️ ' + e.message);
      }
      setMsg(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };

  return (
    <div>
      <button
        onClick={() => { window.location.href = '/brand-kit'; }}
        style={{ width: '100%', padding: '10px', fontSize: 13, fontWeight: 600, border: '0.5px solid #0F6E56', borderRadius: 8, background: '#fff', color: '#0F6E56', cursor: 'pointer', marginBottom: 12 }}
      >
        🎨 {t('brand_kit')}
      </button>
      {myProducts && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>
            {myProducts.current_count} / {myProducts.max_products} products used ({myProducts.plan} plan · {({free:5,basic:5,starter:4,pro:3,unlimited:2})[String(myProducts.plan||'free').toLowerCase()] ?? 5}% fee)
          </p>
          <button onClick={onProductAdded} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
            🔄 Refresh
          </button>
        </div>
      )}

      {myProducts && myProducts.products.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: '1.5rem' }}>
          {myProducts.products.map((p) => (
            <div key={p.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: 8 }}>
              <div style={{ width: '100%', aspectRatio: '1', background: '#F1EFE8', borderRadius: 6, marginBottom: 6, overflow: 'hidden' }}>
                {p.image && <img src={p.image} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              </div>
              <button onClick={() => setQuickEditFor(p)}
                style={{ fontSize: 11, padding: '3px 9px', border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer', marginRight: 6 }}>
                Edit
              </button>
            {p.product_type === 'virtual' && p.digital_kind && p.digital_kind !== 'shared' && (
              <button onClick={() => setCodesFor(p)}
                style={{ fontSize: 11, padding: '3px 9px', border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer', marginLeft: 6 }}>
                🎟️ {t('dig_codes')}
              </button>
            )}
              <p style={{ fontSize: 12, fontWeight: 500, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
              <p style={{ fontSize: 12, color: colors.primary, margin: 0 }}>{p.price_pi} Pi</p>
              <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{p.stock} in stock · {p.orders_count} sold</p>
              <button
                onClick={() => boostProduct(p.id, p.name)}
                disabled={boosting[p.id] || p.is_boosted}
                style={{
                  width: '100%', marginTop: 6, padding: '4px', fontSize: 11, fontWeight: 500, border: 'none', borderRadius: 4,
                  background: p.is_boosted ? '#FFF3C4' : colors.primaryBg, color: p.is_boosted ? '#8A6D00' : colors.primary,
                  cursor: p.is_boosted ? 'default' : 'pointer',
                }}
              >
                {p.is_boosted ? '📢 Boosted' : boosting[p.id] ? 'Processing...' : '📢 Boost (1 Pi / 7d)'}
              </button>
            </div>
          ))}
        </div>
      )}

      {quickEditFor && (
        <QuickEditProduct
          product={quickEditFor}
          onClose={() => setQuickEditFor(null)}
          onSaved={() => { setQuickEditFor(null); onProductAdded(); }}
        />
      )}

      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 12px' }}>{t('add_product')}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={labelStyle}>{t('product_name')}</label>
            <input style={inputStyle} value={form.name} onChange={(e) => { update('name')(e); if (!catTouched) { const g = guessCategory(e.target.value); setCatSuggest(g); if (g) setForm((f) => ({ ...f, category: g, product_type: g === 'services' ? 'service' : (DIGITAL_CATS.includes(g) ? 'virtual' : f.product_type) })); } }} placeholder="Handmade mug" />
          </div>
          <div>
            <label style={labelStyle}>{t('description')}</label>
            <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.description} onChange={update('description')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'start' }}>
            <div>
              <label style={labelStyle}>{t('mm_price_label')}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={priceCurrency}
                  onChange={(e) => {
                    const cur = e.target.value; setPriceCurrency(cur);
                    if (cur === 'PI') { setForm((f) => ({ ...f, price_pi: priceInput })); if (priceInput > 0) { getPiConversion(parseFloat(priceInput)).then((cv) => setPricePreview(cv ? { fromPi: true, usd: cv.usd, local: cv.local, sym: cv.localSymbol } : null)); } else setPricePreview(null); }
                    else if (priceInput) { toPiFrom(priceInput, cur).then((r) => { if (r) { setForm((f) => ({ ...f, price_pi: r.pi, price_usd: r.usd })); setPricePreview(r); } }); }
                  }}
                  style={{ ...inputStyle, width: 78, flexShrink: 0, padding: '9px 4px' }}>
                  {CURRENCY_LIST.map((x) => <option key={x.code} value={x.code}>{x.symbol} {x.label}</option>)}
                </select>
                <input style={inputStyle} type="number" step="0.01" value={priceInput}
                  onChange={(e) => {
                    const v = e.target.value; setPriceInput(v);
                    if (priceCurrency === 'PI') { setForm((f) => ({ ...f, price_pi: v })); if (v > 0) { getPiConversion(parseFloat(v)).then((cv) => setPricePreview(cv ? { fromPi: true, usd: cv.usd, local: cv.local, sym: cv.localSymbol } : null)); } else setPricePreview(null); }
                    else if (v) { toPiFrom(v, priceCurrency).then((r) => { if (r) { setForm((f) => ({ ...f, price_pi: r.pi, price_usd: r.usd })); setPricePreview(r); } }); }
                    else { setForm((f) => ({ ...f, price_pi: '' })); setPricePreview(null); }
                  }}
                  placeholder={t('mm_price_ph')} />
              </div>
              {pricePreview && (
                <p style={{ fontSize: 11.5, color: colors.primary, margin: '5px 0 0' }}>
                  {pricePreview.fromPi
                    ? `= $${pricePreview.usd}${pricePreview.local ? ` · ${pricePreview.sym}${pricePreview.local}` : ''}`
                    : `= π ${pricePreview.pi} · $${pricePreview.usd}`}
                </p>
              )}
              {priceCurrency !== 'PI' && (
                <p style={{ fontSize: 10.5, color: colors.textMuted, margin: '3px 0 0' }}>{t('mm_price_note')}</p>
              )}
            </div>
            <div>
              <label style={labelStyle}>{t('stock_label')}</label>
              <input style={inputStyle} type="number" value={form.stock} onChange={update('stock')} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>{t('category_label')}</label>
            <select style={inputStyle} value={CATEGORIES.some((x) => x.key === form.category) ? form.category : (form.category ? '__custom' : '')}
              onChange={(e) => {
                setCatTouched(true);
                if (e.target.value === '__custom') { setForm((f) => ({ ...f, category: '' })); }
                else {
                  const cat = e.target.value;
                  const nextType = cat === 'services' ? 'service' : (DIGITAL_CATS.includes(cat) ? 'virtual' : form.product_type);
                  setForm((f) => ({ ...f, category: cat, product_type: nextType }));
                }
              }}>
              <option value="">{t('cat_choose')}</option>
              {CATEGORIES.map((x) => <option key={x.key} value={x.key}>{x.icon} {x.label}</option>)}
              <option value="__custom">✏️ {t('cat_custom')}</option>
            </select>
            {catSuggest && !catTouched && (
              <p style={{ fontSize: 11, color: colors.primary, margin: '-6px 0 8px' }}>
                ✨ {t('cat_suggested')}
              </p>
            )}
            {(catTouched && !CATEGORIES.some((x) => x.key === form.category)) && (
              <input style={inputStyle} value={form.category} onChange={update('category')}
                placeholder={t('cat_type_own')} maxLength={100} />
            )}
          </div>
          <div>
            <label style={labelStyle}>{t('product_type')}</label>
            <select style={inputStyle} value={form.product_type} onChange={update('product_type')}>
              <option value="physical">📦 Physical — you ship it (clothes, food, electronics)</option>
              <option value="virtual">💾 Digital — delivered online (ebook, code, file, voucher)</option>
                            <option value="service">🔧 {t('ptype_service')}</option>
              </select>
            <p style={{ fontSize: 11, color: colors.textMuted, margin: '4px 0 0', lineHeight: 1.5 }}>
              {form.product_type === 'physical'
                ? '📦 Needs a courier and the buyer\'s address. Stock runs out. Clothes, food, mugs, electronics, handmade items.'
                : form.product_type === 'service' ? '🔧 You do the work in person or online - no courier, no codes. Haircut, tailoring, repair, tuition, cleaning.' : '💾 No courier, no address, never runs out. Ebook, course, design file, license key, voucher, software.'}
            </p>
          </div>
          {form.product_type === 'physical' && (
            <div>
              <label style={labelStyle}>Shipping fee (Pi) - flat rate added at checkout</label>
              <input style={inputStyle} type="number" step="0.01" value={form.shipping_fee_pi} onChange={update('shipping_fee_pi')} placeholder="0 for free shipping" />
            </div>
          )}
          {form.product_type === 'virtual' && (
            <div>
              <label style={labelStyle}>{t('dig_delivery')}</label>
              <select style={inputStyle} value={form.digital_kind} onChange={update('digital_kind')}>
                <option value="shared">📄 {t('dig_kind_shared')}</option>
                <option value="unique">🔑 {t('dig_kind_unique')}</option>
                <option value="scratch">🎟️ {t('dig_kind_scratch')}</option>
              </select>
              <p style={{ fontSize: 11, color: colors.textMuted, margin: '4px 0 10px', lineHeight: 1.5 }}>
                {form.digital_kind === 'shared' ? t('dig_help_shared') : t('dig_help_codes')}
              </p>
              <label style={labelStyle}>
                {form.digital_kind === 'shared' ? t('dig_content_label') : t('dig_instructions_label')}
              </label>
              <textarea
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace' }}
                rows={3}
                value={form.digital_content}
                onChange={update('digital_content')}
                placeholder="e.g. Redemption code: XXXX-XXXX-XXXX, or a download link"
              />
              <p style={{ fontSize: 11, color: colors.textMuted, margin: '4px 0 0' }}>
                Delivered to the buyer automatically as soon as they pay. Note: with 1 stock unit and one
                shared code, that code goes to whoever buys it first - for multiple buyers, add separate
                stock/listings per code, or update this after each sale.
              </p>
            </div>
          )}
          <div>
            <label style={labelStyle}>{t('photo_label')}</label>
            <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
          </div>
        </div>
        {msg && <p style={{ fontSize: 12, color: msg.includes('added') ? colors.primary : colors.danger, marginBottom: 8 }}>{msg}</p>}
        <button
          onClick={submit}
          disabled={submitting}
          style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: submitting ? '#B4B2A9' : colors.primary, color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer' }}
        >
          {submitting ? t('mm_adding') : t('add_product')}
        </button>
      </div>
      {codesFor && <CodesPanel product={codesFor} onClose={() => setCodesFor(null)} />}
    </div>
  );
}

function WalletPanel() {
  const [balance, setBalance] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [showLedger, setShowLedger] = useState(false);
  const [ledger, setLedger] = useState([]);

  const load = useCallback(async () => {
    try {
      const [balRes, listRes] = await Promise.all([
        fetch(`${API_BASE}/wallet/balance/`, { headers: authHeaders() }),
        fetch(`${API_BASE}/wallet/withdrawals/`, { headers: authHeaders() }),
      ]);
      if (balRes.ok) setBalance(await balRes.json());
      if (listRes.ok) {
        const data = await listRes.json();
        setWithdrawals(data.withdrawals);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLedger = async () => {
    if (!showLedger) {
      try {
        const res = await fetch(`${API_BASE}/wallet/ledger/`, { headers: authHeaders() });
        if (res.ok) {
          const data = await safeJson(res);
          setLedger(data.entries);
        }
      } catch (e) {}
    }
    setShowLedger((s) => !s);
  };

  const submit = async () => {
    setSubmitting(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/wallet/withdraw/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ amount: parseFloat(amount), pi_wallet_address: walletAddress }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit request.');
      alert('Withdrawal request submitted.');
      setAmount('');
      setWalletAddress('');
      load();
    } catch (e) {
      alert(e.message);
      setMsg(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (balance === null) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_wallet')}</p>;

  return (
    <div>
      <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem', marginBottom: '1.5rem' }}>
        <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>{t('available_to_withdraw')}</p>
        <p style={{ fontSize: 26, fontWeight: 600, margin: '0 0 4px', color: colors.primary }}>{balance.available_balance_pi} Pi</p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 8px' }}>Total earned all-time (before 5% platform fee): {balance.total_earned_pi} Pi</p>
        <button onClick={toggleLedger} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
          {showLedger ? 'Hide' : 'View'} ledger
        </button>
        {showLedger && (
          <div style={{ marginTop: 10, borderTop: `0.5px solid ${colors.border}`, paddingTop: 10 }}>
            {ledger.length === 0 ? (
              <p style={{ fontSize: 12, color: colors.textMuted }}>{t('no_sales_yet')}</p>
            ) : (
              ledger.map((e) => (
                <div key={e.order_id} style={{ fontSize: 12, padding: '6px 0', borderBottom: `0.5px solid ${colors.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>#{e.tracking_code || e.order_id} · @{e.buyer}</span>
                    <span>{e.net_pi} Pi net</span>
                  </div>
                  <p style={{ color: colors.textMuted, margin: '2px 0 0' }}>
                    {e.items.map((it) => `${it.quantity}x ${it.name}`).join(', ')} — gross {e.gross_pi} Pi, fee {e.commission_pi} Pi
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 12px' }}>{t('request_withdrawal')}</p>
        <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
          <input
            type="number" step="0.000001" placeholder={t('amount_pi')}
            value={amount} onChange={(e) => setAmount(e.target.value)}
            style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }}
          />
          <input
            type="text" placeholder={t('your_wallet_address')}
            value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)}
            style={{ padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }}
          />
        </div>
        <button
          onClick={submit}
          disabled={submitting || !amount || !walletAddress}
          style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}
        >
          {submitting ? 'Submitting...' : 'Request withdrawal'}
        </button>
        {msg && <p style={{ fontSize: 12, color: colors.danger, marginTop: 8 }}>{msg}</p>}
      </div>

      {withdrawals.length > 0 && (
        <div>
          <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 8px' }}>{t('withdrawal_history')}</p>
          {withdrawals.map((w) => (
            <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0', borderBottom: `0.5px solid ${colors.border}` }}>
              <span>{w.amount} Pi</span>
              <span style={{ color: w.status === 'approved' ? colors.primary : w.status === 'rejected' ? colors.danger : colors.textMuted }}>
                {w.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MyOrdersPanel({ isMerchant }) {
  const [digitalFor, setDigitalFor] = useState(null);
  const confirmReceived = async (orderId) => {
    if (!window.confirm(t('escrow_confirm_received'))) return;
    try {
      const r = await fetch(`${API_BASE}/orders/${orderId}/received/`, { method: 'POST', headers: authHeaders() });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d.error || 'Failed');
      alert(t('escrow_released_msg'));
      load();
    } catch (e) { alert(networkMessage(e)); }
  };

  const reportNotReceived = async (orderId) => {
    const reason = window.prompt(t('escrow_dispute_reason'));
    if (reason === null) return;
    try {
      const r = await fetch(`${API_BASE}/orders/${orderId}/not-received/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ reason }),
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d.error || 'Failed');
      alert(t('escrow_dispute_msg'));
      load();
    } catch (e) { alert(networkMessage(e)); }
  };

  const [view, setView] = useState('buyer');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});

  const STATUS_FLOW = ['paid', 'processing', 'shipped', 'delivered', 'cancelled'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = view === 'buyer' ? 'orders/mine/' : 'orders/merchant/';
      const res = await fetch(`${API_BASE}/${endpoint}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await safeJson(res);
        setOrders(data.orders);
      }
    } catch (e) {} finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (orderId, newStatus) => {
    setUpdating((u) => ({ ...u, [orderId]: true }));
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/status/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not update status.');
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setUpdating((u) => ({ ...u, [orderId]: false }));
    }
  };

  const viewDigitalContent = async (orderId) => {
    try {
      const res = await fetch(`${API_BASE}/orders/${orderId}/digital-content/`, { headers: authHeaders() });
      const data = await safeJson(res);
      if (!res.ok) {
        alert(data.error || 'No digital content for this order.');
        return;
      }
      const text = data.items.map((it) => `${it.product_name} (x${it.quantity}):\n${it.digital_content}`).join('\n\n---\n\n');
      alert(text);
    } catch (e) {
      alert('Could not load digital content.');
    }
  };

  const leaveReview = async (productId, productName) => {    const ratingStr = prompt(`Rate "${productName}" from 1 to 5:`, '5');
    if (!ratingStr) return;
    const rating = parseInt(ratingStr, 10);
    if (!rating || rating < 1 || rating > 5) {
      alert('Please enter a number from 1 to 5.');
      return;
    }
    const comment = prompt('Add a comment (optional):', '') || '';

    try {
      const res = await fetch(`${API_BASE}/products/${productId}/review/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ rating, comment }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not submit review.');
      alert('Thanks for your review!');
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        {isMerchant ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setView('buyer')}
              style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: view === 'buyer' ? colors.primaryBg : '#fff', color: view === 'buyer' ? colors.primary : colors.textMuted, cursor: 'pointer' }}
            >
              My purchases
            </button>
            <button
              onClick={() => setView('merchant')}
              style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: view === 'merchant' ? colors.primaryBg : '#fff', color: view === 'merchant' ? colors.primary : colors.textMuted, cursor: 'pointer' }}
            >
              Store orders
            </button>
          </div>
        ) : <span />}
        <button onClick={load} style={{ fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer' }}>
          🔄 Refresh
        </button>
      </div>

      {loading ? (
        <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading')}</p>
      ) : orders.length === 0 ? (
        <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_orders_yet')}</p>
      ) : (
        orders.map((o) => (
          <div key={o.id} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                {view === 'buyer' ? o.merchant_name : `@${o.buyer}`}
                {o.tracking_code && <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 400 }}> · {o.tracking_code}</span>}
              </span>
              <span style={{ fontSize: 12, color: o.status === 'paid' ? colors.primary : colors.textMuted }}>{o.status}</span>
            </div>
            {o.items.map((it, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0' }}>{it.quantity}x {it.name} — {it.price_pi} Pi each</p>
                {view === 'buyer' && o.status === 'delivered' && (
                  <button
                    onClick={() => leaveReview(it.product_id, it.name)}
                    style={{ fontSize: 11, padding: '2px 8px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer' }}
                  >
                    ⭐ Review
                  </button>
                )}
              </div>
            ))}
            <p style={{ fontSize: 13, fontWeight: 600, margin: '4px 0 0' }}>Total: {o.total_pi} Pi</p>
            {view === 'buyer' && o.escrow_status === 'held' && o.status !== 'pending' && o.status !== 'cancelled' && (
              <div style={{ marginTop: 8, padding: '8px 10px', background: '#F4F3EE', borderRadius: 8 }}>
                <p style={{ fontSize: 11, color: colors.textMuted, margin: '0 0 6px' }}>🔒 {t('escrow_held_note')}</p>
                <button onClick={() => confirmReceived(o.id)}
                  style={{ fontSize: 12, padding: '6px 12px', border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer', marginRight: 6 }}>
                  ✅ {t('escrow_received')}
                </button>
                <button onClick={() => reportNotReceived(o.id)}
                  style={{ fontSize: 12, padding: '6px 12px', border: '0.5px solid #C0392B', borderRadius: 8, background: '#fff', color: '#C0392B', cursor: 'pointer' }}>
                  ⚠️ {t('escrow_not_received')}
                </button>
              </div>
            )}
            {o.escrow_status === 'disputed' && <p style={{ fontSize: 11, color: '#C0392B', margin: '6px 0 0' }}>⚠️ {t('escrow_disputed')}</p>}
            {o.escrow_status === 'released' && <p style={{ fontSize: 11, color: '#0F6E56', margin: '6px 0 0' }}>✅ {t('escrow_released_tag')}</p>}
            {o.escrow_status === 'refunded' && <p style={{ fontSize: 11, color: colors.textMuted, margin: '6px 0 0' }}>↩️ {t('escrow_refunded_tag')}</p>}
            {view === 'buyer' && o.status !== 'pending' && o.status !== 'cancelled' && (o.items || []).some((it) => it.product_type === 'virtual') && (
              <button
                onClick={() => setDigitalFor(o.id)}
                style={{ marginTop: 6, fontSize: 11, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer' }}
              >
                💾 View digital content
              </button>
            )}
            {view === 'merchant' && STATUS_FLOW.includes(o.status) && (
              <select
                value={o.status}
                disabled={updating[o.id]}
                onChange={(e) => updateStatus(o.id, e.target.value)}
                style={{ marginTop: 8, fontSize: 12, padding: '4px 8px', borderRadius: 6, border: `0.5px solid ${colors.border}` }}
              >
                {STATUS_FLOW.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
        ))
      )}
      {digitalFor && <DigitalItemsModal orderId={digitalFor} onClose={() => setDigitalFor(null)} />}
    </div>
  );
}

function InventoryPanel() {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/inventory/`, { headers: authHeaders() });
      if (res.ok) {
        const data = await safeJson(res);
        setItems(data.inventory);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  if (items === null) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_inventory')}</p>;

  const totalStock = items.reduce((s, i) => s + i.stock, 0);
  const totalSold = items.reduce((s, i) => s + i.sold, 0);
  const totalRevenue = items.reduce((s, i) => s + i.revenue_pi, 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, flex: 1 }}>
          <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 8, padding: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{totalStock}</p>
            <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{t('in_stock')}</p>
          </div>
          <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 8, padding: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{totalSold}</p>
            <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{t('sold_label')}</p>
          </div>
          <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 8, padding: 10, textAlign: 'center' }}>
            <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{totalRevenue.toFixed(2)}</p>
            <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{t('pi_revenue')}</p>
          </div>
        </div>
        <button onClick={load} style={{ marginLeft: 8, fontSize: 12, padding: '4px 10px', border: `0.5px solid ${colors.border}`, borderRadius: 6, background: '#fff', cursor: 'pointer', height: 'fit-content' }}>
          🔄
        </button>
      </div>

      {items.map((p) => (
        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 6 }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>{p.name} {p.is_boosted && '📢'}</p>
            <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{p.is_active ? 'Active' : 'Inactive'}</p>
          </div>
          <div style={{ textAlign: 'right', fontSize: 12, color: colors.textMuted }}>
            <p style={{ margin: 0 }}>{p.stock} in stock</p>
            <p style={{ margin: 0 }}>{p.sold} sold · {p.revenue_pi} Pi</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function VoucherPanel() {
  const [vouchers, setVouchers] = useState([]);
  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/vouchers/mine/`, { headers: authHeaders() });
      if (res.ok) {
        const data = await safeJson(res);
        setVouchers(data.vouchers);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/vouchers/create/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ code, discount_percent: parseFloat(discount) }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not create voucher.');
      alert('Voucher created!');
      setCode('');
      setDiscount('');
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem' }}>
        <p style={{ fontWeight: 500, fontSize: 14, margin: '0 0 12px' }}>{t('create_voucher')}</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            placeholder="CODE (e.g. SUMMER10)"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            style={{ flex: 2, padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }}
          />
          <input
            type="number" placeholder="% off"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            style={{ flex: 1, padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6 }}
          />
        </div>
        <button
          onClick={submit}
          disabled={submitting || !code || !discount}
          style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}
        >
          {submitting ? 'Creating...' : 'Create voucher'}
        </button>
      </div>

      {vouchers.map((v) => (
        <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 0', borderBottom: `0.5px solid ${colors.border}` }}>
          <span style={{ fontFamily: 'monospace' }}>{v.code}</span>
          <span>{v.discount_percent}% off · {v.used_count}/{v.max_uses} used</span>
        </div>
      ))}
    </div>
  );
}

function TrackOrderPanel() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState('');

  const track = async () => {
    setMsg('');
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/track/${encodeURIComponent(code)}/`);
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Order not found.');
      setResult(data);
    } catch (e) {
      setMsg(e.message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          placeholder={t('enter_tracking_code') + ' (e.g. XC000001)'}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 20 }}
        />
        <button onClick={track} style={{ padding: '0 20px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 20, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          Track
        </button>
      </div>
      {msg && <p style={{ color: colors.danger, fontSize: 13 }}>{msg}</p>}
      {result && (
        <div style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1rem' }}>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 4px' }}>{result.merchant_name}</p>
          <p style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px', color: colors.primary, textTransform: 'capitalize' }}>{result.status}</p>
          {result.estimated_delivery && (
            <p style={{ fontSize: 13, margin: '0 0 8px' }}>Estimated delivery: {result.estimated_delivery}</p>
          )}
          {result.items.map((it, i) => (
            <p key={i} style={{ fontSize: 13, color: colors.textMuted, margin: '2px 0' }}>{it.quantity}x {it.name}</p>
          ))}
          <p style={{ fontSize: 14, fontWeight: 600, marginTop: 8 }}>Total: {result.total_pi} Pi</p>
        </div>
      )}
    </div>
  );
}

function DealsPanel({ onUseCode }) {
  const [deals, setDeals] = useState(null);

  const [dealErr, setDealErr] = useState('');
  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/vouchers/public/`);
      const data = await safeJson(res).catch(() => ({}));
      if (res.ok) { setDeals(Array.isArray(data.deals) ? data.deals : []); }
      else { setDeals([]); setDealErr(data.error || `Could not load deals (${res.status})`); }
    } catch (e) { setDeals([]); setDealErr('Could not reach the server.'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (deals === null) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_deals')}</p>;

  if (deals.length === 0) {
    return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{dealErr || t('no_active_deals')}</p>;
  }

  return (
    <div>
      {deals.map((d) => (
        <div key={d.code} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8 }}>
          <div>
            <p style={{ fontSize: 15, fontWeight: 600, color: colors.primary, margin: 0, fontFamily: 'monospace' }}>{d.code}</p>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0 0' }}>{d.discount_percent}% off at {d.merchant_name} · {d.uses_remaining} left</p>
          </div>
          <button
            onClick={() => onUseCode(d.code)}
            style={{ fontSize: 12, padding: '6px 12px', border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer' }}
          >
            Use in cart
          </button>
        </div>
      ))}
    </div>
  );
}

function MarketplaceHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 20 };
  const headingStyle = { fontSize: 16, fontWeight: 600, margin: '0 0 8px' };
  const textStyle = { fontSize: 14, color: '#3A3935', lineHeight: 1.6, margin: '0 0 8px' };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 640, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>How the Marketplace works</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>What is this?</p>
          <p style={textStyle}>A marketplace where Pi merchants sell physical and virtual products directly to buyers, paid entirely in Pi.</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>If you want to shop</p>
          <p style={textStyle}>1. Browse products, filter by category, or search. If you've set interests in your PenFlow profile, matching products are shown first with a "✨ For you" badge.</p>
          <p style={textStyle}>2. Add items to your cart. For physical items, you'll need to provide a phone number and delivery address at checkout (saved on your device for next time).</p>
          <p style={textStyle}>3. Check the "Deals" tab for active voucher codes, or apply one manually in your cart.</p>
          <p style={textStyle}>4. Tap "Checkout with Pi" and confirm the payment in Pi Browser. Each merchant in your cart is paid as a separate transaction.</p>
          <p style={textStyle}>5. Track physical orders anytime with their tracking code under the "Track" tab - no login needed. Virtual products deliver their content instantly under "My purchases".</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>If you want to sell</p>
          <p style={textStyle}>1. Go to "Become a Merchant" from the Shopping hub and submit your business details. An admin reviews and approves it.</p>
          <p style={textStyle}>2. Once approved, add products from the "My Products" tab - set a price, stock, category, and whether it's physical (shipped) or virtual (instant digital delivery).</p>
          <p style={textStyle}>3. Optionally set a store-wide discount percentage when you register - it's applied automatically to every product you sell, on top of any voucher a buyer uses.</p>
          <p style={textStyle}>4. Create voucher codes from the "Vouchers" tab to run limited-time promotions - buyers can find them in the public "Deals" tab.</p>
          <p style={textStyle}>5. When an order comes in, update its status from "Orders" as you process, ship, and deliver it - the buyer gets an email when it ships.</p>
          <p style={textStyle}>6. Track your earnings in the "Wallet" tab, with a full ledger of every sale. Request a withdrawal anytime.</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>Fees</p>
          <p style={textStyle}>Buyers pay the listed price (minus any store discount or voucher) plus shipping. The platform takes a 5% commission when an order is marked paid - merchants receive 95% net.</p>
        </div>

        <div style={sectionStyle}>
          <p style={headingStyle}>FAQ</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>Where does my Pi payment actually go?</p>
          <p style={textStyle}>To the platform's own Pi wallet first, then tracked internally as the merchant's balance. Payouts to merchants are manual for now - see below.</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>Why are merchant payouts manual instead of automatic?</p>
          <p style={textStyle}>Pi Network's automatic App-to-User payment API isn't yet available for real (Mainnet) Pi. Until it is, an admin reviews and manually sends withdrawal requests.</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>Can I get a refund?</p>
          <p style={textStyle}>There's no automated refund flow yet - contact the merchant directly, or reach out to platform support for a manual resolution.</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>What happens if a voucher code doesn't work?</p>
          <p style={textStyle}>It may be expired, inactive, or have reached its usage limit - the checkout total simply won't reflect the discount, and no error blocks your order.</p>
          <p style={{ ...textStyle, fontWeight: 600, marginBottom: 2 }}>Do I need Pi Browser to shop?</p>
          <p style={textStyle}>Yes, for payments. You can browse products in a regular browser, but "Rent with Pi"/"Checkout with Pi" requires Pi Browser.</p>
        </div>

        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
          Got it
        </button>
      </div>
    </div>
  );
}

export default function MerchantMarketplace() {
  const [tab, setTab] = useState('browse');
  const [products, setProducts] = useState([]);
  const visibleProducts = tab === 'services' ? products.filter((p) => p.product_type === 'service') : products.filter((p) => p.product_type !== 'service');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [personalized, setPersonalized] = useState(false);
  const [cart, setCart] = useState({ items: [], total_pi: 0 });
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutMsg, setCheckoutMsg] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [shippingPhone, setShippingPhone] = useState(() => localStorage.getItem('shipping_phone') || '');
  const [shippingAddress, setShippingAddress] = useState(() => localStorage.getItem('shipping_address') || '');
  const [buyerEmail, setBuyerEmail] = useState(() => localStorage.getItem('buyer_email') || '');
  const [myProducts, setMyProducts] = useState(null);
  const [isMerchant, setIsMerchant] = useState(false);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedCategory) params.set('category', selectedCategory);
      const res = await fetch(`${API_BASE}/products/?${params.toString()}`, { headers: getAuthToken() ? authHeaders() : {} });
      const data = await safeJson(res);
      setProducts(data.products || []);
      setPersonalized(!!data.personalized);
    } catch (e) {
      // fail silently, list stays empty
    } finally {
      setLoading(false);
    }
  }, [search, selectedCategory]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/products/categories/`);
      if (res.ok) {
        const data = await safeJson(res);
        setCategories(data.categories);
      }
    } catch (e) {}
  }, []);

  const loadCart = useCallback(async () => {
    if (!getAuthToken()) return;
    try {
      const res = await fetch(`${API_BASE}/cart/`, { headers: authHeaders() });
      if (res.ok) setCart(await safeJson(res));
    } catch (e) {}
  }, []);

  const loadMyProducts = useCallback(async () => {
    if (!getAuthToken()) return;
    try {
      const res = await fetch(`${API_BASE}/products/mine/`, { headers: authHeaders() });
      if (res.ok) {
        setMyProducts(await safeJson(res));
        setIsMerchant(true);
      } else {
        setIsMerchant(false);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { localStorage.setItem('shipping_phone', shippingPhone); }, [shippingPhone]);
  useEffect(() => { localStorage.setItem('shipping_address', shippingAddress); }, [shippingAddress]);
  useEffect(() => { localStorage.setItem('buyer_email', buyerEmail); }, [buyerEmail]);
  useEffect(() => { loadCart(); loadMyProducts(); }, [loadCart, loadMyProducts]);

  const addToCart = async (product) => {
    if (!getAuthToken()) { alert('Please login first!'); return; }
    await fetch(`${API_BASE}/cart/add/`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ product_id: product.id, quantity: 1 }),
    });
    loadCart();
  };

  const updateQty = async (productId, qty) => {
    await fetch(`${API_BASE}/cart/update/`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ product_id: productId, quantity: qty }),
    });
    loadCart();
  };

  const removeItem = async (productId) => {
    await fetch(`${API_BASE}/cart/remove/`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ product_id: productId }),
    });
    loadCart();
  };

  const checkout = async () => {
    if (!window.Pi) {
      setCheckoutMsg('Open this page inside Pi Browser to pay with Pi.');
      return;
    }
    setCheckingOut(true);
    setCheckoutMsg('');

    try {
      await window.Pi.authenticate(['username', 'payments'], (payment) => {
        console.log('Incomplete payment found during re-auth:', payment);
      });

      const res = await fetch(`${API_BASE}/checkout/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ voucher_code: voucherCode, shipping_phone: shippingPhone, shipping_address: shippingAddress, email: buyerEmail }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Checkout failed.');

      // Pay for each merchant's order one at a time (Pi payments are single transactions)
      for (const order of data.orders) {
        await new Promise((resolve, reject) => {
          window.Pi.createPayment(
            {
              amount: order.total_pi,
              memo: `Order from ${order.merchant_name}`,
              metadata: { order_id: order.order_id },
            },
            {
              onReadyForServerApproval: (paymentId) => {
                fetch(`${API_BASE}/orders/${order.order_id}/approve/`, {
                  method: 'POST',
                  headers: authHeaders(),
                  body: JSON.stringify({ payment_id: paymentId }),
                });
              },
              onReadyForServerCompletion: (paymentId, txid) => {
                fetch(`${API_BASE}/orders/${order.order_id}/complete/`, {
                  method: 'POST',
                  headers: authHeaders(),
                  body: JSON.stringify({ payment_id: paymentId, txid }),
                })
                  .then(() => resolve())
                  .catch(reject);
              },
              onCancel: () => reject(new Error('Payment cancelled.')),
              onError: (error) => reject(new Error(error?.message || 'Payment error.')),
            }
          );
        });
      }

      setCheckoutMsg('Order(s) paid successfully!');
      loadCart();
      loadProducts();
    } catch (e) {
      setCheckoutMsg(e.message);
    } finally {
      setCheckingOut(false);
    }
  };

  const tabStyle = (t) => ({
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    border: 'none',
    borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent',
    background: 'transparent',
    color: tab === t ? colors.primary : colors.textMuted,
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  });

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <MarketplaceHelpModal onClose={() => setShowHelp(false)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>🛍️ Marketplace</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('shop_tagline')}</p>
        </div>
        <button
          onClick={() => setShowHelp(true)}
          style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer', flexShrink: 0 }}
        >
          ❓ How it works
        </button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem' }}>
        <button style={tabStyle('browse')} onClick={() => setTab('browse')}>{t('browse')}</button>
        <button style={tabStyle('services')} onClick={() => setTab('services')}>🔧 {t('services_tab')}</button>
        <button style={tabStyle('cart')} onClick={() => setTab('cart')}>
          Cart {cart.items.length > 0 && `(${cart.items.length})`}
        </button>
        {isMerchant && (
          <button style={tabStyle('products')} onClick={() => setTab('products')}>{t('my_products')}</button>
        )}
        {isMerchant && (
          <button style={tabStyle('profit')} onClick={() => setTab('profit')}>{t('mprof_tab')}</button>
        )}
        <button style={tabStyle('orders')} onClick={() => setTab('orders')}>{t('orders')}</button>
        <button style={tabStyle('track')} onClick={() => setTab('track')}>{t('track')}</button>
        <button style={tabStyle('deals')} onClick={() => setTab('deals')}>{t('deals')}</button>
        {isMerchant && (
          <button style={tabStyle('wallet')} onClick={() => setTab('wallet')}>{t('wallet')}</button>
        )}
        {isMerchant && (
          <button style={tabStyle('inventory')} onClick={() => setTab('inventory')}>{t('inventory')}</button>
        )}
        {isMerchant && (
          <button style={tabStyle('vouchers')} onClick={() => setTab('vouchers')}>{t('vouchers')}</button>
        )}
      </div>

      {(tab === 'browse' || tab === 'services') && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem' }}>
            <input
              placeholder="🔍 Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, padding: '10px 14px', fontSize: 14, border: `0.5px solid #B4B2A9`, borderRadius: 20, boxSizing: 'border-box' }}
            />
            <button onClick={loadProducts} style={{ padding: '0 14px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>
              🔄
            </button>
          </div>

          {categories.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: '0.5rem' }}>
              <button
                onClick={() => setSelectedCategory('')}
                style={{
                  flexShrink: 0, padding: '5px 12px', fontSize: 12, borderRadius: 14, whiteSpace: 'nowrap',
                  border: `0.5px solid ${colors.border}`, cursor: 'pointer',
                  background: selectedCategory === '' ? colors.primary : '#fff',
                  color: selectedCategory === '' ? '#fff' : colors.textMuted,
                }}
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c.name}
                  onClick={() => setSelectedCategory(c.name)}
                  style={{
                    flexShrink: 0, padding: '5px 12px', fontSize: 12, borderRadius: 14, whiteSpace: 'nowrap',
                    border: `0.5px solid ${colors.border}`, cursor: 'pointer',
                    background: selectedCategory === c.name ? colors.primary : '#fff',
                    color: selectedCategory === c.name ? '#fff' : colors.textMuted,
                  }}
                >
                  {c.name} ({c.count})
                </button>
              ))}
            </div>
          )}

          {personalized && (
            <p style={{ fontSize: 12, color: colors.primary, margin: '0 0 0.75rem' }}>
              ✨ Sorted based on your interests - matching products marked below.
            </p>
          )}

          {loading ? (
            <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_products')}</p>
          ) : visibleProducts.length === 0 ? (
            <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>No products found.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {visibleProducts.map((p) => (
                <ProductCard key={p.id} product={p} onAddToCart={addToCart} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'cart' && (
        <div>
          <CartPanel
            cart={cart}
            onUpdateQty={updateQty}
            onRemove={removeItem}
            onCheckout={checkout}
            checkingOut={checkingOut}
            voucherCode={voucherCode}
            setVoucherCode={setVoucherCode}
            shippingPhone={shippingPhone}
            setShippingPhone={setShippingPhone}
            shippingAddress={shippingAddress}
            setShippingAddress={setShippingAddress} buyerEmail={buyerEmail} setBuyerEmail={setBuyerEmail}
          />
          {checkoutMsg && <p style={{ fontSize: 13, color: colors.textMuted, marginTop: 10, textAlign: 'center' }}>{checkoutMsg}</p>}
        </div>
      )}

      {tab === 'products' && isMerchant && (
        <MyProductsPanel myProducts={myProducts} onProductAdded={loadMyProducts} />
      )}

      {tab === 'orders' && <MyOrdersPanel isMerchant={isMerchant} />}

      {tab === 'wallet' && isMerchant && <WalletPanel />}

      {tab === 'inventory' && isMerchant && <InventoryPanel />}

      {tab === 'vouchers' && isMerchant && <VoucherPanel />}

      {tab === 'track' && <TrackOrderPanel />}

      {tab === 'deals' && (
        <DealsPanel
          onUseCode={(code) => {
            setVoucherCode(code);
            setTab('cart');
          }}
        />
      )}
    </div>
  );
}


function QuickEditProduct({ product, onClose, onSaved }) {
  const [f, setF] = useState({
    name: product.name || '', price_pi: product.price_pi || '', stock: product.stock ?? 1,
    cost_price_pi: product.cost_price_pi || '', supplier_name: product.supplier_name || '',
    digital_kind: product.digital_kind || 'shared', is_active: product.is_active !== false,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const upd = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const inp = { width: '100%', padding: '9px 11px', fontSize: 14, border: `0.5px solid ${colors.border}`, borderRadius: 8, boxSizing: 'border-box', marginBottom: 8 };
  const lbl = { fontSize: 12, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 4 };
  const save = async () => {
    setBusy(true); setMsg('');
    try {
      const body = {
        name: f.name, price_pi: parseFloat(f.price_pi) || 0, stock: parseInt(f.stock, 10) || 0,
        cost_price_pi: parseFloat(f.cost_price_pi) || 0, supplier_name: f.supplier_name,
        is_active: f.is_active,
      };
      if (product.product_type === 'virtual') body.digital_kind = f.digital_kind;
      const res = await fetch(`${API_BASE}/products/${product.id}/update/`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await safeJson(res).catch(() => ({})); throw new Error(d.error || 'Save failed'); }
      onSaved();
    } catch (e) { setMsg(e.message); }
    setBusy(false);
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'grid', placeItems: 'center', zIndex: 200, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: '1.1rem', maxWidth: 380, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
        <p style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>Edit: {product.name}</p>
        <label style={lbl}>Name</label>
        <input style={inp} value={f.name} onChange={upd('name')} />
        <label style={lbl}>Price (Pi)</label>
        <input style={inp} type="number" step="0.01" value={f.price_pi} onChange={upd('price_pi')} />
        {product.product_type !== 'service' && (
          <>
            <label style={lbl}>Stock</label>
            <input style={inp} type="number" value={f.stock} onChange={upd('stock')} />
          </>
        )}
        {product.product_type === 'virtual' && (
          <>
            <label style={lbl}>How is it delivered?</label>
            <select style={inp} value={f.digital_kind} onChange={upd('digital_kind')}>
              <option value="shared">Same content for everyone</option>
              <option value="unique">One code per buyer</option>
              <option value="scratch">Scratch card - one per buyer</option>
            </select>
          </>
        )}
        <label style={lbl}>{t('mprof_cost')}</label>
        <input style={inp} type="number" step="0.01" value={f.cost_price_pi} onChange={upd('cost_price_pi')} placeholder="0" />
        <p style={{ fontSize: 11, color: colors.textMuted, margin: '-4px 0 8px' }}>{t('mprof_cost_help')}</p>
        <label style={lbl}>{t('mprof_supplier')}</label>
        <input style={inp} value={f.supplier_name} onChange={upd('supplier_name')} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={f.is_active} onChange={(e) => setF((x) => ({ ...x, is_active: e.target.checked }))} />
          Active (visible to buyers)
        </label>
        {msg && <p style={{ fontSize: 12, color: '#C0392B', margin: '0 0 8px' }}>{msg}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} disabled={busy} style={{ flex: 1, padding: '10px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? '...' : 'Save changes'}</button>
          <button onClick={onClose} style={{ padding: '10px 16px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}